import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, GripVertical } from 'lucide-react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoatsAdmin = () => {
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBoat, setEditingBoat] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const emptyBoat = {
    name: '',
    description: '',
    prices: [{ size: '', price: '' }],
    images: [''],
    features: [],
    sizes: '',
    externalLink: '',
    order: 0,
    active: true
  };

  useEffect(() => {
    fetchBoats();
  }, []);

  const fetchBoats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/boats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoats(response.data);
    } catch (err) {
      console.error('Failed to fetch boats', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Clean up empty prices and images
      const cleanedBoat = {
        ...editingBoat,
        prices: editingBoat.prices.filter(p => p.size && p.price),
        images: editingBoat.images.filter(img => img),
        features: editingBoat.features?.filter(f => f) || []
      };

      if (editingBoat.id) {
        await axios.put(`${API}/admin/boats/${editingBoat.id}`, cleanedBoat, { headers });
      } else {
        await axios.post(`${API}/admin/boats`, cleanedBoat, { headers });
      }
      
      setShowModal(false);
      setEditingBoat(null);
      fetchBoats();
    } catch (err) {
      alert('Failed to save boat: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this boat model?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/boats/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBoats();
    } catch (err) {
      alert('Failed to delete boat');
    }
  };

  const addPrice = () => {
    setEditingBoat({
      ...editingBoat,
      prices: [...editingBoat.prices, { size: '', price: '' }]
    });
  };

  const updatePrice = (index, field, value) => {
    const newPrices = [...editingBoat.prices];
    newPrices[index][field] = value;
    setEditingBoat({ ...editingBoat, prices: newPrices });
  };

  const removePrice = (index) => {
    const newPrices = editingBoat.prices.filter((_, i) => i !== index);
    setEditingBoat({ ...editingBoat, prices: newPrices });
  };

  const addImage = () => {
    setEditingBoat({
      ...editingBoat,
      images: [...editingBoat.images, '']
    });
  };

  const updateImage = (index, value) => {
    const newImages = [...editingBoat.images];
    newImages[index] = value;
    setEditingBoat({ ...editingBoat, images: newImages });
  };

  const removeImage = (index) => {
    const newImages = editingBoat.images.filter((_, i) => i !== index);
    setEditingBoat({ ...editingBoat, images: newImages });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="boats-admin-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase">Boat Models</h1>
            <p className="text-slate-500 mt-1">Manage your boat inventory</p>
          </div>
          <button
            onClick={() => { setEditingBoat(emptyBoat); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-boat-btn"
          >
            <Plus className="w-5 h-5" />
            Add Boat
          </button>
        </div>

        {/* Boats List */}
        <div className="space-y-4">
          {boats.map((boat) => (
            <div key={boat.id} className="bg-white border-2 border-slate-100 p-6 flex gap-6" data-testid={`boat-item-${boat.id}`}>
              <div className="w-32 h-24 bg-slate-100 flex-shrink-0 overflow-hidden">
                {boat.images[0] && (
                  <img src={boat.images[0]} alt={boat.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900">{boat.name}</h3>
                    <p className="text-slate-500 text-sm mt-1 line-clamp-2">{boat.description}</p>
                    <div className="flex gap-2 mt-2">
                      {boat.prices.slice(0, 3).map((p, i) => (
                        <span key={i} className="text-xs bg-slate-100 px-2 py-1 text-slate-600">
                          {p.size}: {p.price}
                        </span>
                      ))}
                      {boat.prices.length > 3 && (
                        <span className="text-xs text-slate-400">+{boat.prices.length - 3} more</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 ${boat.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {boat.active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={() => { setEditingBoat(boat); setShowModal(true); }}
                      className="p-2 text-slate-400 hover:text-orange-500"
                      data-testid={`edit-boat-${boat.id}`}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(boat.id)}
                      className="p-2 text-slate-400 hover:text-red-500"
                      data-testid={`delete-boat-${boat.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {boats.length === 0 && (
          <div className="bg-white border-2 border-slate-100 p-12 text-center">
            <p className="text-slate-500">No boat models yet. Add your first boat!</p>
          </div>
        )}

        {/* Edit Modal */}
        {showModal && editingBoat && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="boat-modal">
            <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex items-center justify-between">
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase">
                  {editingBoat.id ? 'Edit Boat' : 'Add Boat'}
                </h2>
                <button onClick={() => { setShowModal(false); setEditingBoat(null); }} className="p-2 text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Name */}
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Name *</label>
                  <input
                    type="text"
                    value={editingBoat.name}
                    onChange={(e) => setEditingBoat({ ...editingBoat, name: e.target.value })}
                    className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                    placeholder="e.g., 14'-20' Armageddon V Bottom"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Description *</label>
                  <textarea
                    value={editingBoat.description}
                    onChange={(e) => setEditingBoat({ ...editingBoat, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                    placeholder="Boat description..."
                  />
                </div>

                {/* Prices */}
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Pricing</label>
                  {editingBoat.prices.map((price, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={price.size}
                        onChange={(e) => updatePrice(index, 'size', e.target.value)}
                        className="flex-1 h-10 px-3 border-2 border-slate-200 bg-slate-50 text-sm"
                        placeholder="Size (e.g., 14')"
                      />
                      <input
                        type="text"
                        value={price.price}
                        onChange={(e) => updatePrice(index, 'price', e.target.value)}
                        className="flex-1 h-10 px-3 border-2 border-slate-200 bg-slate-50 text-sm"
                        placeholder="Price (e.g., $8,400)"
                      />
                      <button onClick={() => removePrice(index)} className="p-2 text-red-500 hover:bg-red-50">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={addPrice} className="text-sm text-orange-500 hover:text-orange-600">
                    + Add Price Option
                  </button>
                </div>

                {/* Images */}
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Images (URLs)</label>
                  {editingBoat.images.map((img, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={img}
                        onChange={(e) => updateImage(index, e.target.value)}
                        className="flex-1 h-10 px-3 border-2 border-slate-200 bg-slate-50 text-sm"
                        placeholder="Image URL"
                      />
                      <button onClick={() => removeImage(index)} className="p-2 text-red-500 hover:bg-red-50">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={addImage} className="text-sm text-orange-500 hover:text-orange-600">
                    + Add Image
                  </button>
                </div>

                {/* Additional Sizes */}
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Available Sizes (text)</label>
                  <input
                    type="text"
                    value={editingBoat.sizes || ''}
                    onChange={(e) => setEditingBoat({ ...editingBoat, sizes: e.target.value })}
                    className="w-full h-10 px-4 border-2 border-slate-200 bg-slate-50"
                    placeholder="e.g., 18', 21', 23', 24'"
                  />
                </div>

                {/* External Link */}
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">External Link (optional)</label>
                  <input
                    type="text"
                    value={editingBoat.externalLink || ''}
                    onChange={(e) => setEditingBoat({ ...editingBoat, externalLink: e.target.value })}
                    className="w-full h-10 px-4 border-2 border-slate-200 bg-slate-50"
                    placeholder="https://..."
                  />
                </div>

                {/* Active */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={editingBoat.active}
                    onChange={(e) => setEditingBoat({ ...editingBoat, active: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label className="text-slate-700">Active (visible on website)</label>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-slate-100 p-6 flex justify-end gap-4">
                <button
                  onClick={() => { setShowModal(false); setEditingBoat(null); }}
                  className="px-6 py-3 text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                  <Save className="w-5 h-5" />
                  Save Boat
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BoatsAdmin;
